import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaPhoneAlt, FaArrowLeft, FaEdit, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaSave, FaTimes, FaUser, FaGlobe, FaBriefcase,
  FaPlus, FaClock, FaBuilding
} from 'react-icons/fa';
import './SheriffOfficeDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DispatchCenterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [center, setCenter] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedCenter, setEditedCenter] = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    title: '',
    phone: '',
    phone_ext: '',
    email: '',
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchCenterDetails();
  }, [id]);

  const fetchCenterDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/dispatch-centers/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCenter(data.data);
        setEditedCenter(data.data);
        setStaff(data.data.staff || []);
        setError('');
      } else {
        setError('Dispatch center not found');
      }
    } catch (err) {
      console.error('Error fetching center details:', err);
      setError('Failed to load dispatch center details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCenterEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/dispatch-centers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedCenter)
      });

      const data = await response.json();

      if (data.success) {
        setCenter(data.data);
        setEditedCenter(data.data);
        setEditMode(false);
      } else {
        alert('Failed to update dispatch center');
      }
    } catch (err) {
      console.error('Error updating center:', err);
      alert('Failed to update dispatch center');
    }
  };

  const handleCancelEdit = () => {
    setEditedCenter(center);
    setEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setEditedCenter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.title) {
      alert('Name and title are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/dispatch-centers/${id}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newStaff)
      });

      const data = await response.json();

      if (data.success) {
        // Refresh center details to get updated staff list
        await fetchCenterDetails();
        // Reset form and close modal
        setNewStaff({
          name: '',
          title: '',
          phone: '',
          phone_ext: '',
          email: '',
          notes: ''
        });
        setShowAddStaffModal(false);
      } else {
        alert('Failed to add staff member');
      }
    } catch (err) {
      console.error('Error adding staff:', err);
      alert('Failed to add staff member');
    }
  };

  const handleStaffInputChange = (field, value) => {
    setNewStaff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getTitleBadge = (title) => {
    if (!title) return null;

    const titleLower = title.toLowerCase();
    let color = '#7f8c8d';

    if (titleLower.includes('director') || titleLower.includes('chief')) {
      color = '#2c3e50';
    } else if (titleLower.includes('supervisor') || titleLower.includes('manager')) {
      color = '#34495e';
    } else if (titleLower.includes('dispatcher')) {
      color = '#3498db';
    }

    return (
      <span className="title-badge" style={{ backgroundColor: color }}>
        {title}
      </span>
    );
  };

  const getAgencyTypeBadge = (agencyType) => {
    if (!agencyType) return null;

    const colors = {
      'Sheriff': '#2c3e50',
      'Police': '#3498db',
      'Consolidated': '#9b59b6',
      'State': '#16a085',
      'Federal': '#c0392b',
      'Tribal': '#d68910'
    };

    const color = colors[agencyType] || '#7f8c8d';

    return (
      <span className="county-badge-large" style={{ background: color }}>
        {agencyType}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="office-detail-container">
        <div className="loading">Loading dispatch center details...</div>
      </div>
    );
  }

  if (error || !center) {
    return (
      <div className="office-detail-container">
        <div className="error-message">{error || 'Dispatch center not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/dispatch-centers')}>
          <FaArrowLeft /> Back to Dispatch Centers
        </button>
      </div>
    );
  }

  const displayData = editMode ? editedCenter : center;

  return (
    <div className="office-detail-container">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/address-book/dispatch-centers')}>
          <FaArrowLeft /> Back to Dispatch Centers
        </button>

        <div className="header-title">
          <FaPhoneAlt size={28} style={{ color: '#e74c3c' }} />
          <div>
            <h1>{center.facility_name || center.agency_jurisdiction || 'Unnamed Facility'}</h1>
            <div className="header-badges">
              {center.county_location && (
                <span className="county-badge-large">{center.county_location} County</span>
              )}
              {getAgencyTypeBadge(center.agency_type)}
              {center.is_consolidated && (
                <span className="county-badge-large" style={{ background: '#9b59b6' }}>
                  <FaBuilding /> Consolidated
                </span>
              )}
              {center.operates_24_7 && (
                <span className="county-badge-large" style={{ background: '#27ae60' }}>
                  <FaClock /> 24/7 Operations
                </span>
              )}
            </div>
          </div>
        </div>

        {user.role === 'admin' && !editMode && (
          <button className="btn-edit-office" onClick={() => setEditMode(true)}>
            <FaEdit /> Edit Center Info
          </button>
        )}

        {editMode && (
          <div className="edit-actions">
            <button className="btn-save" onClick={handleSaveCenterEdit}>
              <FaSave /> Save
            </button>
            <button className="btn-cancel" onClick={handleCancelEdit}>
              <FaTimes /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="content-layout">
        {/* Center Information Section */}
        <div className="office-info-section">
          <h2>Dispatch Center Information</h2>

          <div className="info-card-compact">
            <div className="compact-grid">
              {/* ORI - PROMINENT at top */}
              {!editMode && displayData.ori && (
                <div className="compact-item full-width">
                  <div style={{
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    color: 'white',
                    padding: '1.25rem 1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)',
                    border: '3px solid rgba(255, 255, 255, 0.2)',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: '0.9', marginBottom: '0.5rem', letterSpacing: '1.5px' }}>
                      ORI NUMBER
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', letterSpacing: '3px', fontFamily: 'monospace' }}>
                      {displayData.ori}
                    </div>
                  </div>
                </div>
              )}

              {editMode && (
                <div className="compact-item full-width">
                  <label>ORI Number</label>
                  <input
                    type="text"
                    value={displayData.ori || ''}
                    onChange={(e) => handleInputChange('ori', e.target.value)}
                    placeholder="ORI Number"
                  />
                </div>
              )}

              {/* Main Phone (Non-Emergency) - PROMINENT */}
              {!editMode && displayData.main_phone && (
                <div className="compact-item full-width">
                  <div style={{
                    background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                    color: 'white',
                    padding: '1rem 1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
                    border: '3px solid rgba(255, 255, 255, 0.2)',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: '0.9', marginBottom: '0.5rem', letterSpacing: '1.5px' }}>
                      NON-EMERGENCY PHONE
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700', letterSpacing: '2px' }}>
                      {displayData.main_phone}
                    </div>
                  </div>
                </div>
              )}

              <div className="compact-item">
                <label>Facility Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={displayData.facility_name || ''}
                    onChange={(e) => handleInputChange('facility_name', e.target.value)}
                    placeholder="Facility name"
                  />
                ) : (
                  <span>{displayData.facility_name || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>Agency Jurisdiction</label>
                {editMode ? (
                  <input
                    type="text"
                    value={displayData.agency_jurisdiction || ''}
                    onChange={(e) => handleInputChange('agency_jurisdiction', e.target.value)}
                    placeholder="Agency jurisdiction"
                  />
                ) : (
                  <span>{displayData.agency_jurisdiction || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>Agency Type</label>
                {editMode ? (
                  <select
                    value={displayData.agency_type || ''}
                    onChange={(e) => handleInputChange('agency_type', e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="Sheriff">Sheriff</option>
                    <option value="Police">Police</option>
                    <option value="Consolidated">Consolidated</option>
                    <option value="State">State</option>
                    <option value="Federal">Federal</option>
                    <option value="Tribal">Tribal</option>
                  </select>
                ) : (
                  <span>{displayData.agency_type || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>County Location</label>
                {editMode ? (
                  <input
                    type="text"
                    value={displayData.county_location || ''}
                    onChange={(e) => handleInputChange('county_location', e.target.value)}
                    placeholder="County"
                  />
                ) : (
                  <span>{displayData.county_location || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label><FaPhoneAlt /> Main Phone (Non-Emergency)</label>
                {editMode ? (
                  <input
                    type="tel"
                    value={displayData.main_phone || ''}
                    onChange={(e) => handleInputChange('main_phone', e.target.value)}
                    placeholder="Main phone"
                  />
                ) : (
                  <span>{displayData.main_phone || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label><FaClock /> 24/7 Dispatch Phone</label>
                {editMode ? (
                  <input
                    type="tel"
                    value={displayData.dispatch_24hr || ''}
                    onChange={(e) => handleInputChange('dispatch_24hr', e.target.value)}
                    placeholder="24/7 dispatch phone"
                  />
                ) : (
                  <span>{displayData.dispatch_24hr || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label><FaFax /> Fax</label>
                {editMode ? (
                  <input
                    type="tel"
                    value={displayData.fax || ''}
                    onChange={(e) => handleInputChange('fax', e.target.value)}
                    placeholder="Fax number"
                  />
                ) : (
                  <span>{displayData.fax || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item full-width">
                <label><FaEnvelope /> Email</label>
                {editMode ? (
                  <input
                    type="email"
                    value={displayData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Email address"
                  />
                ) : (
                  <span>
                    {displayData.email ? (
                      <a href={`mailto:${displayData.email}`}>{displayData.email}</a>
                    ) : 'N/A'}
                  </span>
                )}
              </div>

              <div className="compact-item full-width">
                <label><FaGlobe /> Website</label>
                {editMode ? (
                  <input
                    type="url"
                    value={displayData.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="Website URL"
                  />
                ) : (
                  <span>
                    {displayData.website ? (
                      <a href={displayData.website} target="_blank" rel="noopener noreferrer">
                        {displayData.website}
                      </a>
                    ) : 'N/A'}
                  </span>
                )}
              </div>

              <div className="compact-item full-width">
                <label><FaMapMarkerAlt /> Physical Address</label>
                {editMode ? (
                  <textarea
                    value={displayData.physical_address || ''}
                    onChange={(e) => handleInputChange('physical_address', e.target.value)}
                    placeholder="Physical address"
                    rows="2"
                  />
                ) : (
                  <span>{displayData.physical_address || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item full-width">
                <label>Mailing Address</label>
                {editMode ? (
                  <textarea
                    value={displayData.mailing_address || ''}
                    onChange={(e) => handleInputChange('mailing_address', e.target.value)}
                    placeholder="Mailing address"
                    rows="2"
                  />
                ) : (
                  <span>{displayData.mailing_address || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>City</label>
                {editMode ? (
                  <input
                    type="text"
                    value={displayData.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                  />
                ) : (
                  <span>{displayData.city || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>State</label>
                {editMode ? (
                  <input
                    type="text"
                    value={displayData.state || ''}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="State"
                  />
                ) : (
                  <span>{displayData.state || 'MT'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>ZIP Code</label>
                {editMode ? (
                  <input
                    type="text"
                    value={displayData.zip || ''}
                    onChange={(e) => handleInputChange('zip', e.target.value)}
                    placeholder="ZIP code"
                  />
                ) : (
                  <span>{displayData.zip || 'N/A'}</span>
                )}
              </div>

              <div className="compact-item">
                <label>Consolidated Dispatch</label>
                {editMode ? (
                  <select
                    value={displayData.is_consolidated ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('is_consolidated', e.target.value === 'true')}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                ) : (
                  <span>{displayData.is_consolidated ? 'Yes' : 'No'}</span>
                )}
              </div>

              {displayData.is_consolidated && (
                <div className="compact-item full-width">
                  <label>Consolidated Details</label>
                  {editMode ? (
                    <textarea
                      value={displayData.consolidated_details || ''}
                      onChange={(e) => handleInputChange('consolidated_details', e.target.value)}
                      placeholder="Details about consolidated operations..."
                      rows="3"
                    />
                  ) : (
                    <span>{displayData.consolidated_details || 'N/A'}</span>
                  )}
                </div>
              )}

              <div className="compact-item">
                <label>Operates 24/7</label>
                {editMode ? (
                  <select
                    value={displayData.operates_24_7 ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('operates_24_7', e.target.value === 'true')}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                ) : (
                  <span>{displayData.operates_24_7 ? 'Yes' : 'No'}</span>
                )}
              </div>
            </div>

            {displayData.notes && (
              <>
                <div className="info-divider"></div>
                <div className="compact-item full-width">
                  <label>Notes</label>
                  {editMode ? (
                    <textarea
                      value={displayData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder="Additional notes..."
                      rows="4"
                    />
                  ) : (
                    <p className="notes-text">{displayData.notes}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Staff Section */}
        <div className="office-staff-section">
          <div className="staff-header">
            <h2>Staff Contacts</h2>
            {user.role === 'admin' && (
              <button className="btn-add-staff" onClick={() => setShowAddStaffModal(true)}>
                <FaPlus /> Add Staff
              </button>
            )}
          </div>

          <div className="staff-list">
            {staff.length === 0 ? (
              <div className="no-staff-info">
                <FaUser size={48} />
                <p>No staff contacts</p>
              </div>
            ) : (
              staff.map((member) => (
                <div key={member.id} className="staff-card">
                  <div className="staff-card-header">
                    <div className="staff-name-section">
                      <h3>{member.name}</h3>
                    </div>
                    {getTitleBadge(member.title)}
                  </div>

                  <div className="staff-contact-info">
                    {member.phone && (
                      <div className="staff-contact-item">
                        <FaPhoneAlt className="staff-icon" />
                        <span>
                          {member.phone}
                          {member.phone_ext && ` ext. ${member.phone_ext}`}
                        </span>
                      </div>
                    )}

                    {member.email && (
                      <div className="staff-contact-item">
                        <FaEnvelope className="staff-icon" />
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      </div>
                    )}

                    {member.notes && (
                      <div className="staff-notes">
                        {member.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="modal-overlay" onClick={() => setShowAddStaffModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Staff Member</h2>
              <button className="modal-close" onClick={() => setShowAddStaffModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => handleStaffInputChange('name', e.target.value)}
                  placeholder="Enter staff member name"
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newStaff.title}
                  onChange={(e) => handleStaffInputChange('title', e.target.value)}
                  placeholder="e.g., Dispatch Director, Dispatcher, Supervisor"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newStaff.phone}
                    onChange={(e) => handleStaffInputChange('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Extension</label>
                  <input
                    type="text"
                    value={newStaff.phone_ext}
                    onChange={(e) => handleStaffInputChange('phone_ext', e.target.value)}
                    placeholder="Ext."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => handleStaffInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newStaff.notes}
                  onChange={(e) => handleStaffInputChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddStaffModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAddStaff}>
                <FaPlus /> Add Staff Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchCenterDetail;
